// =========================================================================
// Valoración inicial · perfil clínico y antropométrico
//
// Va DESPUÉS de aceptar los consentimientos, nunca antes: aquí se recogen
// datos de salud (categoría especial del artículo 9 del RGPD) y el amparo es
// el consentimiento `health_data_processing`.
//
// Todo es voluntario y todo se puede omitir. Cada campo vacío se guarda como
// ausente, no como cero: un peso 0 o una lista vacía "confirmada" son
// afirmaciones que la persona no ha hecho.
//
// Lo que la persona marque aquí decide si entra directa al programa o si su
// alta pasa por revisión humana. Ver `features/progreso/objetivos.ts`.
//
// ⚠️ ESTILOS EN LÍNEA A PROPÓSITO. En este repo Tailwind solo se aplica bajo
// `.ui-modern`, y las pantallas de autenticación (AuthScreens) NO lo están:
// con clases de Tailwind este formulario sale sin fondo y con el texto
// ilegible sobre el degradado. Se comprobó en el navegador.
// =========================================================================
import { useState, type CSSProperties } from 'react';
import { COMORBILIDADES, etiquetaDe, otrasDesdeTexto } from '@/features/progreso/comorbilidades';
import { evaluarBloqueo, type Sexo } from '@/features/progreso/objetivos';
import { confirmarRegistro, guardarBorradorRegistro } from '@/features/progreso/almacen';
import { reintentarSincronizacionValoracion } from '@/features/progreso/sincronizacion-valoracion';
import type { DatosDeclarados } from '@/features/progreso/registro-inicial';

/**
 * Alias del tipo del módulo de progreso: la forma del dato declarado la
 * define quien lo sella, no la pantalla que lo pinta.
 */
export type PerfilInicial = DatosDeclarados;

interface Props {
  /** Persiste la declaración. NO avanza: guardar y continuar son dos cosas. */
  onGuardar: (perfil: PerfilInicial) => void;
  /** Avanza a la aplicación. Se llama por separado y a veces no se llama. */
  onFin: () => void;
  guardando?: boolean;
}

/**
 * Pantalla de parada. Aparece cuando la valoración queda bloqueada y NO ofrece
 * seguir al programa: el proceso se detiene aquí y lo retoma una persona.
 *
 * El tono importa tanto como la regla. Quien llega a esta pantalla acaba de
 * contar algo difícil; si lo que recibe suena a expulsión, la lección que
 * aprende es a no contarlo la próxima vez.
 */
function Parada({ mensaje, onCerrar }: { mensaje: string; onCerrar: () => void }) {
  return (
    <div style={{
      background: '#fff', borderRadius: 18, padding: 22, textAlign: 'left',
      boxShadow: '0 12px 32px -12px rgba(0,0,0,.35)',
    }}>
      <div style={{ fontSize: 17, fontWeight: 800, color: TINTA }}>
        Vamos a parar aquí un momento
      </div>
      <p style={{ margin: '10px 0 0', fontSize: 13, lineHeight: 1.6, color: TINTA }}>
        {mensaje}
      </p>
      <p style={{ margin: '14px 0 0', fontSize: 12, lineHeight: 1.6, color: GRIS }}>
        Puedes seguir usando la aplicación para registrar tus comidas y tu estado
        de ánimo. Lo que no vamos a hacer es proponerte un objetivo de peso
        automático.
      </p>
      <button type="button" onClick={onCerrar}
        style={{
          marginTop: 20, width: '100%', borderRadius: 12, border: 'none',
          background: AZUL, color: '#fff', padding: '13px 16px',
          fontSize: 14, fontWeight: 800, cursor: 'pointer',
        }}>
        Entendido
      </button>
    </div>
  );
}

const numeroOUndefined = (v: string): number | undefined => {
  const n = Number(v);
  return v.trim() !== '' && Number.isFinite(n) && n > 0 ? n : undefined;
};

const TINTA = '#17202a';
const GRIS = '#566573';
const AZUL = '#2B8DD6';

const etiqueta: CSSProperties = {
  display: 'block', fontSize: 11, fontWeight: 700, letterSpacing: '.06em',
  textTransform: 'uppercase', color: GRIS, marginBottom: 6,
};

const campo: CSSProperties = {
  width: '100%', boxSizing: 'border-box', borderRadius: 10,
  border: '1px solid #cbd5e1', padding: '9px 10px', fontSize: 14,
  color: TINTA, background: '#fff',
};

function chip(activa: boolean): CSSProperties {
  return {
    borderRadius: 999, padding: '7px 12px', fontSize: 12, cursor: 'pointer',
    border: `1px solid ${activa ? AZUL : '#cbd5e1'}`,
    background: activa ? '#e8f2ff' : '#fff',
    color: activa ? AZUL : TINTA,
    fontWeight: activa ? 700 : 400,
    transition: 'all .15s',
  };
}

/**
 * Revisión del autor antes de enviar nada a calcular.
 *
 * Existe porque después de la inscripción el dato queda CERRADO: corregirlo
 * exigirá autorización del administrador. Si la única oportunidad de arreglar
 * un 96 tecleado como 69 es esta, hay que dársela explícitamente en vez de
 * confiar en que nadie se equivoque al teclear.
 */
function Revision({
  perfil, guardando, onCorregir, onConfirmar,
}: {
  perfil: PerfilInicial;
  guardando: boolean;
  onCorregir: () => void;
  onConfirmar: () => void;
}) {
  const fila = (etiq: string, valor: string) => (
    <div key={etiq} style={{
      display: 'flex', justifyContent: 'space-between', gap: 12,
      padding: '9px 0', borderBottom: '1px solid #eef2f6', fontSize: 13,
    }}>
      <span style={{ color: GRIS }}>{etiq}</span>
      <span style={{ color: TINTA, fontWeight: 700, textAlign: 'right' }}>{valor}</span>
    </div>
  );
  const sinDato = '— sin indicar';

  return (
    <div style={{
      background: '#fff', borderRadius: 18, padding: 20, textAlign: 'left',
      maxHeight: '62vh', overflowY: 'auto',
      boxShadow: '0 12px 32px -12px rgba(0,0,0,.35)',
    }}>
      <div style={{ fontSize: 17, fontWeight: 800, color: TINTA }}>Revisa antes de enviar</div>
      <p style={{ margin: '6px 0 14px', fontSize: 12, lineHeight: 1.5, color: GRIS }}>
        Comprueba que es correcto. <strong>Una vez confirmado no podrás cambiarlo
        tú</strong>: para corregirlo después haría falta autorización del
        administrador. Es lo que garantiza que el dato no se manipula.
      </p>

      {fila('Sexo', perfil.sexo ?? sinDato)}
      {fila('Peso', perfil.pesoKg ? `${perfil.pesoKg} kg` : sinDato)}
      {fila('Talla', perfil.tallaCm ? `${perfil.tallaCm} cm` : sinDato)}
      {fila('Cintura', perfil.cinturaCm ? `${perfil.cinturaCm} cm` : sinDato)}
      {fila(
        'Enfermedades',
        perfil.comorbilidades.length
          ? perfil.comorbilidades.map(etiquetaDe).join(', ')
          : 'ninguna indicada',
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
        <button type="button" disabled={guardando} onClick={onCorregir}
          style={{
            borderRadius: 12, border: '1px solid #cbd5e1', background: '#fff',
            color: TINTA, padding: '13px 16px', fontSize: 14, cursor: 'pointer',
          }}>
          Corregir
        </button>
        <button type="button" disabled={guardando} onClick={onConfirmar}
          style={{
            flex: 1, borderRadius: 12, border: 'none', background: AZUL, color: '#fff',
            padding: '13px 16px', fontSize: 14, fontWeight: 800,
            opacity: guardando ? 0.6 : 1, cursor: 'pointer',
          }}>
          {guardando ? 'Guardando…' : 'Confirmar y enviar'}
        </button>
      </div>
    </div>
  );
}

export function PerfilClinicoInicial({ onGuardar, onFin, guardando = false }: Props) {
  // El formulario NO envía directo a calcular: se revisa primero. Después de
  // la inscripción el dato queda cerrado, así que este es el único momento en
  // que la persona puede corregir un error de tecleo sin pedir permiso a nadie.
  const [fase, setFase] = useState<'formulario' | 'revision' | 'parada'>('formulario');
  const [mensajeParada, setMensajeParada] = useState('');
  const [sexo, setSexo] = useState<Sexo | undefined>(undefined);
  const [peso, setPeso] = useState('');
  const [talla, setTalla] = useState('');
  const [cintura, setCintura] = useState('');
  const [marcadas, setMarcadas] = useState<string[]>([]);
  const [otras, setOtras] = useState('');

  const alternar = (id: string) =>
    setMarcadas((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const construir = (): PerfilInicial => ({
    sexo,
    pesoKg: numeroOUndefined(peso),
    tallaCm: numeroOUndefined(talla),
    cinturaCm: numeroOUndefined(cintura),
    comorbilidades: [...marcadas, ...otrasDesdeTexto(otras)],
  });

  /**
   * Confirmar el registro. A partir de aquí el dato queda cerrado: se guarda
   * SIEMPRE, incluso cuando el caso se bloquea, porque es justo lo que tiene
   * que leer el profesional que va a revisarlo.
   *
   * Lo que el bloqueo impide es CALCULAR y continuar, no declarar.
   */
  const confirmar = () => {
    const perfil = construir();
    // Se SELLA aquí: a partir de este punto solo lo toca un administrador con
    // autorización, y cada cambio deja rastro. Ver `registro-inicial.ts`.
    confirmarRegistro(perfil, new Date().toISOString());

    // Y se sella también en el servidor, que es quien lo impone. Se hace sin
    // bloquear la pantalla: si la red falla, la persona no se queda atrapada
    // en un formulario, y el registro local ya está sellado. Si el envío
    // falla, no se pierde: queda pendiente y `App.tsx` lo reintenta en el
    // próximo inicio de sesión (`sincronizacion-valoracion.ts`, ADR-020).
    void reintentarSincronizacionValoracion();

    onGuardar(perfil);

    const bloqueo = evaluarBloqueo({
      edad: 0, // la edad no la pide este formulario; el bloqueo por texto y
               // por declaración no la necesitan, y el antropométrico se
               // vuelve a evaluar con el perfil completo al entrar.
      pesoKg: perfil.pesoKg ?? 0,
      tallaCm: perfil.tallaCm ?? 0,
      comorbilidades: perfil.comorbilidades,
    });

    if (bloqueo) {
      setMensajeParada(bloqueo.mensaje);
      setFase('parada');
      return;
    }
    onFin();
  };

  if (fase === 'parada') return <Parada mensaje={mensajeParada} onCerrar={onFin} />;

  if (fase === 'revision') {
    return (
      <Revision
        perfil={construir()}
        guardando={guardando}
        onCorregir={() => setFase('formulario')}
        onConfirmar={confirmar}
      />
    );
  }

  return (
    <div style={{
      background: '#fff', borderRadius: 18, padding: 20, textAlign: 'left',
      maxHeight: '62vh', overflowY: 'auto',
      boxShadow: '0 12px 32px -12px rgba(0,0,0,.35)',
    }}>
      <div style={{ fontSize: 17, fontWeight: 800, color: TINTA }}>Cuéntanos un poco de ti</div>
      <p style={{ margin: '6px 0 0', fontSize: 12, lineHeight: 1.5, color: GRIS }}>
        Es <strong>voluntario</strong> y puedes omitirlo. Nos sirve para entender tu punto de
        partida y para saber si te conviene que un profesional revise tu caso antes de empezar.
        Son datos de salud: se tratan con la protección que exige esa categoría y no se comparten
        con terceros.
      </p>

      <fieldset style={{ border: 0, padding: 0, margin: '18px 0 0' }}>
        <legend style={etiqueta}>Sexo</legend>
        <div style={{ display: 'flex', gap: 8 }}>
          {(['mujer', 'hombre'] as const).map((s) => (
            <button key={s} type="button" aria-pressed={sexo === s}
              onClick={() => setSexo(sexo === s ? undefined : s)}
              style={{
                ...chip(sexo === s), flex: 1, borderRadius: 10, padding: '9px 12px',
                fontSize: 14, textTransform: 'capitalize',
              }}>
              {s}
            </button>
          ))}
        </div>
        <p style={{ margin: '6px 0 0', fontSize: 11, color: GRIS }}>
          Solo cambia el perímetro abdominal de referencia. Si no lo indicas, no se muestra ninguno.
        </p>
      </fieldset>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginTop: 18 }}>
        {/* Etiqueta corta y unidad dentro del campo: "Cintura (cm)" parte en
            dos líneas en móvil y desalinea los tres inputs. Visto en pantalla. */}
        <div>
          <label style={etiqueta} htmlFor="vi-peso">Peso</label>
          <input id="vi-peso" type="number" inputMode="decimal" step="0.1" min={1}
            placeholder="kg" value={peso} onChange={(e) => setPeso(e.target.value)} style={campo} />
        </div>
        <div>
          <label style={etiqueta} htmlFor="vi-talla">Talla</label>
          <input id="vi-talla" type="number" inputMode="decimal" step="1" min={1}
            placeholder="cm" value={talla} onChange={(e) => setTalla(e.target.value)} style={campo} />
        </div>
        <div>
          <label style={etiqueta} htmlFor="vi-cintura">Cintura</label>
          <input id="vi-cintura" type="number" inputMode="decimal" step="0.5" min={1}
            placeholder="cm" value={cintura} onChange={(e) => setCintura(e.target.value)} style={campo} />
        </div>
      </div>

      <fieldset style={{ border: 0, padding: 0, margin: '20px 0 0' }}>
        <legend style={etiqueta}>¿Tienes alguna de estas?</legend>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
          {COMORBILIDADES.map((c) => (
            <button key={c.id} type="button" aria-pressed={marcadas.includes(c.id)}
              onClick={() => alternar(c.id)} style={chip(marcadas.includes(c.id))}>
              {c.etiqueta}
            </button>
          ))}
        </div>
        <div style={{ marginTop: 14 }}>
          <label style={etiqueta} htmlFor="vi-otras">Otra</label>
          <input id="vi-otras" type="text" value={otras} onChange={(e) => setOtras(e.target.value)}
            placeholder="Sepáralas con comas" style={campo} />
        </div>
      </fieldset>

      <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
        <button type="button" disabled={guardando}
          onClick={() => { guardarBorradorRegistro(construir()); setFase('revision'); }}
          style={{
            flex: 1, borderRadius: 12, border: 'none', background: AZUL, color: '#fff',
            padding: '13px 16px', fontSize: 14, fontWeight: 800,
            opacity: guardando ? 0.6 : 1, cursor: 'pointer',
          }}>
          Revisar
        </button>
        <button type="button" disabled={guardando}
          onClick={() => { onGuardar({ comorbilidades: [] }); onFin(); }}
          style={{
            borderRadius: 12, border: 'none', background: 'transparent', color: GRIS,
            padding: '13px 16px', fontSize: 14, opacity: guardando ? 0.6 : 1, cursor: 'pointer',
          }}>
          Omitir
        </button>
      </div>
    </div>
  );
}

export default PerfilClinicoInicial;
